import { Select } from "@/components/ui/select";
import { Filter as FilterIcon, ArrowUpDown, List as ListIcon, LayoutGrid } from "lucide-react";
import { cn } from "@/lib/cn";

export type ViewMode = "list" | "kanban";

export interface SortOption {
  value: string;
  label: string;
}

interface Props {
  /** Whether the filter sidebar is currently visible. */
  filtersOpen: boolean;
  onToggleFilters: () => void;

  sortBy: string;
  sortDir: "asc" | "desc";
  sortOptions: SortOption[];
  onSortChange: (by: string, dir: "asc" | "desc") => void;

  /** Omit to hide the view-mode toggle. */
  viewMode?: ViewMode;
  onViewModeChange?: (v: ViewMode) => void;

  rightSlot?: React.ReactNode;
}

export function ListToolbar({
  filtersOpen, onToggleFilters,
  sortBy, sortDir, sortOptions, onSortChange,
  viewMode, onViewModeChange,
  rightSlot,
}: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-card px-4 py-2">
      <button
        onClick={onToggleFilters}
        className={cn(
          "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm",
          filtersOpen ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted",
        )}
      >
        <FilterIcon className="h-3.5 w-3.5" />
        Filter
      </button>

      <div className="flex items-center gap-1.5">
        <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
        <Select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value, sortDir)}
          className="h-8 text-sm"
        >
          {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
        <Select
          value={sortDir}
          onChange={(e) => onSortChange(sortBy, e.target.value as "asc" | "desc")}
          className="h-8 w-24 text-sm"
        >
          <option value="desc">Desc</option>
          <option value="asc">Asc</option>
        </Select>
      </div>

      {viewMode && onViewModeChange && (
        <div className="flex overflow-hidden rounded-md border">
          <button
            aria-label="List view"
            onClick={() => onViewModeChange("list")}
            className={cn(
              "flex h-8 w-8 items-center justify-center text-muted-foreground hover:bg-muted",
              viewMode === "list" && "bg-primary/10 text-primary",
            )}
          >
            <ListIcon className="h-4 w-4" />
          </button>
          <button
            aria-label="Kanban view"
            onClick={() => onViewModeChange("kanban")}
            className={cn(
              "flex h-8 w-8 items-center justify-center border-l text-muted-foreground hover:bg-muted",
              viewMode === "kanban" && "bg-primary/10 text-primary",
            )}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">{rightSlot}</div>
    </div>
  );
}
